import './index.scss';
import { Box, Button, IconButton, Modal } from '@mui/material';
import React, {
  FunctionComponent,
  useState,
  useEffect,
  useContext,
} from 'react';
import History from './history';
import Observations from 'components/events/event-state/Observations';
import Documents from './documents';
import checkStage from '../../../assets/icons/shield-check.svg';
import fileWhite from '../../../assets/icons/file-white.svg';
import fileStage from '../../../assets/icons/file-document.svg';
import relojStage from '../../../assets/icons/reloj.svg';
import chatStage from '../../../assets/icons/chat.svg';
import chatWhite from '../../../assets/icons/chat-white.svg';
import calendarIcon from '../../../assets/icons/calendarIcon.svg';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { Rol } from 'types/common';
import Reminders from 'components/events/event-state/Reminders';
import CPEAuditTrail from './CPEAuditTrail';
import { IClaimState } from 'types/claim';
import CPEFlags from './CPEFlags/CPEFlags';
import { ClaimContext } from 'context/claim/ClaimContext';
import SendDocument from './sendDocument';

interface ClaimStepsProps {
  steps: any;
  claim: any;
  CPEClaim: IClaimState;
  uId: any;
}

const iconPaths = {
  CHECK: { selected: checkStage, unselected: checkStage },
  FILE: { selected: fileWhite, unselected: fileStage },
  CHAT: { selected: chatWhite, unselected: chatStage },
  CLOCK: { selected: relojStage, unselected: relojStage },
  CALENDAR: { selected: calendarIcon, unselected: calendarIcon },
};

const STYLES = {
  FLAG: {
    active: {
      color: '#DC3545',
      filter:
        'invert(23%) sepia(92%) saturate(3120%) hue-rotate(343deg) brightness(84%) contrast(97%)',
    },
  },
} as const;

const ClaimSteps: FunctionComponent<ClaimStepsProps> = ({
  steps,
  claim,
  CPEClaim,
  uId,
}): JSX.Element => {
  const { dispatch: claimDispatch } = useContext(ClaimContext);
  const [selectedStep, setSelectedStep] = useState('');
  const [openSendDocument, setOpenSendDocument] = useState(false);
  const [isContentExpanded, setIsContentExpanded] = useState(false);

  useEffect(() => {
    setIsContentExpanded(false);
    setSelectedStep('');
  }, [claim]);

  const handleStepClick = (name: string) => {
    if (selectedStep === name) {
      setSelectedStep('');
      setIsContentExpanded(false);
    } else {
      setSelectedStep(name);
      setIsContentExpanded(true);
    }
  };

  const handleIconClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    setSelectedStep('');
    setIsContentExpanded(false);
  };

  const hasActiveFlags = () =>
    CPEClaim.flags?.some((flag) => flag.status !== 'resolved');

  const getFlagIcon = (
    icon: string | undefined,
    isSelected: boolean,
  ): JSX.Element => {
    if (icon?.toUpperCase() === 'FLAG') {
      const iconPath = iconPaths.CHECK;

      return (
        <img
          src={isSelected ? iconPath.selected : iconPath.unselected}
          alt={icon}
          style={
            hasActiveFlags() ? { filter: STYLES.FLAG.active.filter } : undefined
          }
        />
      );
    }

    const iconPath = iconPaths[icon?.toUpperCase() || ''] || iconPaths.CHECK;
    const { selected, unselected } = iconPath;

    return <img src={isSelected ? selected : unselected} alt={icon || ''} />;
  };

  const getFlagLabelStyle = (icon: string | undefined) => {
    if (icon?.toUpperCase() === 'FLAG') {
      return hasActiveFlags() ? { color: STYLES.FLAG.active.color } : undefined;
    }

    return undefined;
  };

  const getFlagButtonStyle = (icon: string | undefined) => {
    if (icon?.toUpperCase() === 'FLAG' && hasActiveFlags()) {
      return {
        border: `1px solid ${STYLES.FLAG.active.color}`,
        '&.MuiButton-outlined': {
          borderColor: STYLES.FLAG.active.color,
        },
      };
    }

    return undefined;
  };

  const isDisabled =
    Number(localStorage.getItem('rolId')) === Rol.INSURER ||
    Number(localStorage.getItem('rolId')) === Rol.CLAIMS_ASSISTANT;

  return (
    <>
      <Box className="claim-step-info">
        <Box className="claim-step-info">
          {steps?.map((step) => {
            const { id, title, name, icon } = step;
            const isSelected = selectedStep === name;

            return (
              <React.Fragment key={id}>
                <Button
                  key={id}
                  endIcon={getFlagIcon(icon, isSelected)}
                  variant={isSelected ? 'contained' : 'outlined'}
                  className={`claim-step-info__step ${
                    isSelected ? 'claim-step-info__step--selected' : ''
                  } ${
                    isContentExpanded ? 'claim-step-info__step--expanded' : ''
                  }`}
                  onClick={() => handleStepClick(name)}
                  sx={getFlagButtonStyle(icon)}
                >
                  <label style={getFlagLabelStyle(icon)}>{title}</label>
                </Button>
              </React.Fragment>
            );
          })}
          {selectedStep && (
            <IconButton className="close-icon" onClick={handleIconClick}>
              <HighlightOffIcon />
            </IconButton>
          )}
        </Box>
        <Box className="box-content scrollable-container">
          <div style={{ display: selectedStep === 'history' ? '' : 'none' }}>
            <History claim={claim['history']} />
          </div>
          <div
            style={{ display: selectedStep === 'observations' ? '' : 'none' }}
          >
            <div style={{ paddingBottom: '1rem' }}>
              <label>Observaciones</label>
            </div>
            <Observations sicId={claim['info'].sicId} />
          </div>
          <div style={{ display: selectedStep === 'reminders' ? '' : 'none' }}>
            <div style={{ paddingBottom: '1rem' }}>
              <label>Recordatorios</label>
            </div>
            <Reminders sicId={claim['info'].sicId} />
          </div>
          <div style={{ display: selectedStep === 'auditTrail' ? '' : 'none' }}>
            <CPEAuditTrail entries={CPEClaim.auditTrail.entries} />
          </div>
          <div style={{ display: selectedStep === 'flags' ? '' : 'none' }}>
            <CPEFlags
              flags={CPEClaim.flags || []}
              claimDispatch={claimDispatch}
            />
          </div>
          <div style={{ display: selectedStep === 'documents' ? '' : 'none' }}>
            <Documents claim={claim} uId={uId} disabled={isDisabled} />
          </div>
        </Box>
      </Box>
      <Modal
        open={openSendDocument}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box className="claim-step-info__modal">
          <SendDocument
            claim={claim}
            uId={uId}
            // closeModal={()=> setOpenSendDocument(false)}
          />
        </Box>
      </Modal>
    </>
  );
};

export default ClaimSteps;
